import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Check, X, ExternalLink, Pencil, ChevronDown } from 'lucide-react';
import { tasksApi, databasesApi } from '../services/api';
import { useDateTime } from '../contexts/DateTimeContext';
import { useTranslation } from 'react-i18next';

/**
 * Renders and edits a single custom field value inside the tasks table.
 * Manages its own floating editor state (position: fixed panel).
 */
const CustomFieldCell = ({ def, value, taskId, onUpdate }) => {
  const { t } = useTranslation();
  const { formatDate } = useDateTime();
  const [isEditing, setIsEditing] = useState(false);
  const [editorStyle, setEditorStyle] = useState({});
  const [localValue, setLocalValue] = useState(value ?? getDefaultValue(def.field_type));
  const editorRef = useRef(null);
  const cellRef = useRef(null);

  // Keep localValue in sync with prop when the row re-renders
  useEffect(() => {
    setLocalValue(value ?? getDefaultValue(def.field_type));
  }, [value, def.field_type]);

  // Close editor on outside click
  useEffect(() => {
    if (!isEditing) return;
    const handler = (e) => {
      if (editorRef.current && !editorRef.current.contains(e.target) &&
          cellRef.current && !cellRef.current.contains(e.target)) {
        setIsEditing(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isEditing]);

  const openEditor = (e) => {
    e.stopPropagation();
    if (def.field_type === 'checkbox') return; // checkboxes toggle directly
    const rect = e.currentTarget.getBoundingClientRect();
    const editorH = ['select', 'multi_select', 'relation'].includes(def.field_type) ? 280 : 80;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const style = spaceBelow >= editorH
      ? { top: rect.bottom + 4, left: rect.left, minWidth: 200 }
      : { bottom: window.innerHeight - rect.top + 4, left: rect.left, minWidth: 200 };
    setEditorStyle(style);
    setLocalValue(value ?? getDefaultValue(def.field_type));
    setIsEditing(true);
  };

  const save = useCallback(async (newValue) => {
    setIsEditing(false);
    if (newValue === (value ?? getDefaultValue(def.field_type))) return;
    try {
      await tasksApi.update(taskId, { custom_fields: { [def.id]: newValue } });
      onUpdate?.();
    } catch (err) {
      console.error('Failed to update custom field:', err);
    }
  }, [taskId, def.id, value, def.field_type, onUpdate]);

  const toggleCheckbox = async (e) => {
    e.stopPropagation();
    const next = !value;
    try {
      await tasksApi.update(taskId, { custom_fields: { [def.id]: next } });
      onUpdate?.();
    } catch (err) {
      console.error('Failed to toggle checkbox:', err);
    }
  };

  // ── Display rendering ────────────────────────────────────────────────────
  const renderDisplay = () => {
    switch (def.field_type) {
      case 'text':
        return (
          <button
            ref={cellRef}
            onClick={openEditor}
            className="text-xs text-foreground/80 hover:text-foreground truncate max-w-full text-left"
          >
            {value || <span className="text-muted-foreground/70">-</span>}
          </button>
        );

      case 'number':
        return (
          <button
            ref={cellRef}
            onClick={openEditor}
            className="text-xs text-foreground/80 hover:text-foreground"
          >
            {value !== undefined && value !== null && value !== ''
              ? value
              : <span className="text-muted-foreground/70">-</span>}
          </button>
        );

      case 'checkbox':
        return (
          <button
            ref={cellRef}
            onClick={toggleCheckbox}
            className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
              value
                ? 'bg-amber-500 border-amber-500'
                : 'border-input hover:border-input'
            }`}
          >
            {value && <Check className="w-2.5 h-2.5 text-black" />}
          </button>
        );

      case 'date':
        return (
          <button
            ref={cellRef}
            onClick={openEditor}
            className="text-xs text-foreground/80 hover:text-foreground"
          >
            {value
              ? formatDate(new Date(value))
              : <span className="text-muted-foreground/70">-</span>}
          </button>
        );

      case 'select': {
        const opt = def.options?.find(o => o.id === value);
        return (
          <button
            ref={cellRef}
            onClick={openEditor}
            className="text-xs rounded px-2 py-0.5 transition-opacity hover:opacity-75"
            style={opt ? { backgroundColor: `${opt.color}30`, color: opt.color } : { color: '#4B5563' }}
          >
            {opt ? opt.label : '-'}
          </button>
        );
      }

      case 'multi_select': {
        const selected = Array.isArray(value) ? value : [];
        const opts = def.options?.filter(o => selected.includes(o.id)) || [];
        return (
          <button
            ref={cellRef}
            onClick={openEditor}
            className="flex items-center gap-1 flex-wrap"
          >
            {opts.length > 0
              ? opts.map(o => (
                  <span
                    key={o.id}
                    className="text-xs rounded px-1.5 py-0.5"
                    style={{ backgroundColor: `${o.color}30`, color: o.color }}
                  >
                    {o.label}
                  </span>
                ))
              : <span className="text-xs text-muted-foreground/70">-</span>
            }
          </button>
        );
      }

      case 'url':
        return (
          <span ref={cellRef} className="flex items-center gap-1 group/url">
            {value ? (
              <>
                <a
                  href={value.startsWith('http') ? value : `https://${value}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="text-xs text-blue-400 hover:text-blue-300 truncate max-w-[120px]"
                >
                  {value.replace(/^https?:\/\//, '')}
                  <ExternalLink className="w-2.5 h-2.5 inline ml-1" />
                </a>
                <button
                  onClick={openEditor}
                  className="opacity-0 group-hover/url:opacity-100 text-muted-foreground hover:text-foreground transition-all"
                >
                  <Pencil className="w-3 h-3" />
                </button>
              </>
            ) : (
              <button onClick={openEditor} className="text-xs text-muted-foreground/70 hover:text-muted-foreground">
                -
              </button>
            )}
          </span>
        );

      case 'relation':
        return <RelationDisplay def={def} value={value} onClick={openEditor} cellRef={cellRef} />;

      default:
        return null;
    }
  };

  // ── Editor rendering ─────────────────────────────────────────────────────
  const renderEditor = () => {
    if (!isEditing) return null;

    switch (def.field_type) {
      case 'text':
      case 'url':
        return (
          <div
            ref={editorRef}
            className="fixed z-50 bg-popover border border-border rounded-xl shadow-2xl p-3"
            style={editorStyle}
            onClick={e => e.stopPropagation()}
          >
            <input
              autoFocus
              type="text"
              value={localValue || ''}
              onChange={e => setLocalValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') save(localValue || null);
                if (e.key === 'Escape') setIsEditing(false);
              }}
              placeholder={def.field_type === 'url' ? 'https://...' : t('custom_field_cell.enter_value', { name: def.name })}
              className="w-full bg-accent/60 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 outline-none focus:border-amber-500/50"
            />
            <div className="flex justify-end gap-2 mt-2">
              <button
                onClick={() => setIsEditing(false)}
                className="text-xs text-muted-foreground hover:text-foreground px-2 py-1"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => save(localValue || null)}
                className="text-xs bg-amber-500 text-black hover:bg-amber-400 px-3 py-1 rounded-lg font-medium"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        );

      case 'number':
        return (
          <div
            ref={editorRef}
            className="fixed z-50 bg-popover border border-border rounded-xl shadow-2xl p-3"
            style={editorStyle}
            onClick={e => e.stopPropagation()}
          >
            <input
              autoFocus
              type="number"
              value={localValue ?? ''}
              onChange={e => setLocalValue(e.target.value === '' ? null : Number(e.target.value))}
              onKeyDown={e => {
                if (e.key === 'Enter') save(localValue);
                if (e.key === 'Escape') setIsEditing(false);
              }}
              placeholder={t('custom_field_cell.enter_value', { name: def.name })}
              className="w-full bg-accent/60 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 outline-none focus:border-amber-500/50"
            />
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => setIsEditing(false)} className="text-xs text-muted-foreground hover:text-foreground px-2 py-1">{t('common.cancel')}</button>
              <button onClick={() => save(localValue)} className="text-xs bg-amber-500 text-black hover:bg-amber-400 px-3 py-1 rounded-lg font-medium">{t('common.save')}</button>
            </div>
          </div>
        );

      case 'date':
        return (
          <div
            ref={editorRef}
            className="fixed z-50 bg-popover border border-border rounded-xl shadow-2xl p-3"
            style={editorStyle}
            onClick={e => e.stopPropagation()}
          >
            <input
              autoFocus
              type="date"
              value={localValue ? localValue.split('T')[0] : ''}
              onChange={e => setLocalValue(e.target.value || null)}
              onKeyDown={e => {
                if (e.key === 'Enter') save(localValue);
                if (e.key === 'Escape') setIsEditing(false);
              }}
              className="w-full bg-accent/60 border border-border rounded-lg px-3 py-2 text-sm text-foreground outline-none focus:border-amber-500/50"
            />
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => save(null)} className="text-xs text-muted-foreground hover:text-red-400 px-2 py-1">{t('common.clear')}</button>
              <button onClick={() => save(localValue)} className="text-xs bg-amber-500 text-black hover:bg-amber-400 px-3 py-1 rounded-lg font-medium">{t('common.save')}</button>
            </div>
          </div>
        );

      case 'select':
        return (
          <div
            ref={editorRef}
            className="fixed z-50 bg-popover border border-border rounded-xl shadow-2xl py-1 overflow-y-auto"
            style={{ ...editorStyle, maxHeight: 240 }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => save(null)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-accent/60 hover:text-foreground"
            >
              <X className="w-3 h-3" /> {t('common.clear')}
            </button>
            {(def.options || []).map(opt => (
              <button
                key={opt.id}
                onClick={() => save(opt.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                  value === opt.id ? 'bg-accent text-foreground' : 'text-foreground/80 hover:bg-accent/60 hover:text-foreground'
                }`}
              >
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: opt.color }} />
                {opt.label}
                {value === opt.id && <Check className="w-3 h-3 ml-auto text-amber-700 dark:text-amber-400" />}
              </button>
            ))}
          </div>
        );

      case 'multi_select': {
        const selected = Array.isArray(value) ? value : [];
        return (
          <div
            ref={editorRef}
            className="fixed z-50 bg-popover border border-border rounded-xl shadow-2xl py-1 overflow-y-auto"
            style={{ ...editorStyle, maxHeight: 240 }}
            onClick={e => e.stopPropagation()}
          >
            {(def.options || []).map(opt => {
              const isSelected = selected.includes(opt.id);
              return (
                <button
                  key={opt.id}
                  onClick={() => {
                    const next = isSelected
                      ? selected.filter(id => id !== opt.id)
                      : [...selected, opt.id];
                    save(next.length > 0 ? next : null);
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                    isSelected ? 'bg-accent text-foreground' : 'text-foreground/80 hover:bg-accent/60 hover:text-foreground'
                  }`}
                >
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: opt.color }} />
                  {opt.label}
                  {isSelected && <Check className="w-3 h-3 ml-auto text-amber-700 dark:text-amber-400" />}
                </button>
              );
            })}
          </div>
        );
      }

      case 'relation':
        return (
          <RelationEditor
            ref={editorRef}
            def={def}
            value={value}
            editorStyle={editorStyle}
            onSave={save}
            onClose={() => setIsEditing(false)}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      {renderDisplay()}
      {renderEditor()}
    </>
  );
};

function getDefaultValue(fieldType) {
  switch (fieldType) {
    case 'checkbox': return false;
    case 'multi_select': return [];
    case 'relation': return [];
    default: return null;
  }
}

// ─── Relation: display chips + search/picker editor ─────────────────────────

const RelationDisplay = ({ def, value, onClick, cellRef }) => {
  const selected = Array.isArray(value) ? value : (value ? [value] : []);
  const [labels, setLabels] = useState({});
  const labelsRef = useRef(labels);
  labelsRef.current = labels;
  const targetId = def.target_database_id;
  const selectedKey = selected.join(',');
  useEffect(() => {
    const ids = selectedKey ? selectedKey.split(',') : [];
    if (!targetId || ids.length === 0) return;
    if (ids.every(id => id in labelsRef.current)) return;  // every chip already has its label
    let cancelled = false;
    databasesApi.rowsSummary(targetId, '', 500).then(res => {
      if (cancelled) return;
      setLabels(prev => {
        const next = { ...prev };
        for (const r of res.data || []) next[r.id] = r.label;
        return next;
      });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [selectedKey, targetId]);

  return (
    <button
      ref={cellRef}
      onClick={onClick}
      className="flex items-center gap-1 flex-wrap text-left"
    >
      {selected.length > 0 ? (
        selected.map(id => (
          <span key={id} className="inline-flex items-center gap-1 text-xs rounded px-1.5 py-0.5 bg-accent/60 text-foreground border border-border truncate max-w-[140px]">
            <span className="w-1.5 h-1.5 rounded-full bg-purple-400/70 shrink-0" />
            {labels[id] || id.slice(0, 6)}
          </span>
        ))
      ) : (
        <span className="text-xs text-muted-foreground/70">-</span>
      )}
    </button>
  );
};

const RelationEditor = React.forwardRef(({ def, value, editorStyle, onSave, onClose }, ref) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const selected = Array.isArray(value) ? value : (value ? [value] : []);
  const cardinality = def.relation_cardinality || 'many';
  const targetId = def.target_database_id;

  useEffect(() => {
    if (!targetId) return;
    let cancelled = false;
    databasesApi.rowsSummary(targetId, query, 25).then(res => {
      if (!cancelled) setResults(res.data || []);
    }).catch(() => { if (!cancelled) setResults([]); });
    return () => { cancelled = true; };
  }, [targetId, query]);

  if (!targetId) {
    return (
      <div ref={ref} className="fixed z-50 bg-popover border border-border rounded-xl shadow-2xl p-3 text-xs text-muted-foreground"
        style={editorStyle}>
        {t('tasks.relation_no_target', { defaultValue: 'No target database configured.' })}
      </div>
    );
  }

  const toggle = (id) => {
    let next;
    if (cardinality === 'one') {
      next = selected.includes(id) ? [] : [id];
      onSave(next);
      onClose();
      return;
    }
    next = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
    onSave(next.length > 0 ? next : null);
  };

  return (
    <div
      ref={ref}
      className="fixed z-50 bg-popover border border-border rounded-xl shadow-2xl flex flex-col overflow-hidden"
      style={{ ...editorStyle, width: 260, maxHeight: 320 }}
      onClick={e => e.stopPropagation()}
    >
      <div className="px-3 py-2 border-b border-border">
        <input
          autoFocus
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={t('common.search', { defaultValue: 'Search…' })}
          className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground/70 outline-none"
        />
      </div>
      <div className="overflow-y-auto flex-1 py-1">
        {results.length === 0 && (
          <div className="px-3 py-2 text-xs text-muted-foreground">
            {t('tasks.no_rows_found', { defaultValue: 'No rows found.' })}
          </div>
        )}
        {results.map(r => {
          const isSel = selected.includes(r.id);
          return (
            <button
              key={r.id}
              onClick={() => toggle(r.id)}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs ${isSel ? 'bg-accent text-foreground' : 'text-foreground/80 hover:bg-accent/60'}`}
            >
              <span className={`w-3 h-3 rounded border flex items-center justify-center ${isSel ? 'bg-amber-500 border-amber-500' : 'border-input'}`}>
                {isSel && <Check className="w-2.5 h-2.5 text-black" />}
              </span>
              <span className="truncate flex-1">{r.label || <span className="text-muted-foreground">{t('ui_views.custom_field.empty_row')}</span>}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
});

export default CustomFieldCell;
